import { ChevronRightIcon } from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { ReactElement } from 'react'
import { ContactNameIcon } from 'app/components/contacts/ContactNameIcon'
import { Button } from 'app/components/ui/button'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { ContactJson } from 'app/types/entities'
import type { IncomingCall, IncomingCallContact } from 'app/types/incoming-call'
import { callDetailParts } from 'lib/incomingCallDetails'

function ContactMatchCard({ contact, onOpen }: { contact: IncomingCallContact; onOpen: () => void }): ReactElement {
  const name = contact.preferredName || contact.name || `Unnamed contact`
  const lifecycle = contact.lifecycleStage
  const isPatient = lifecycle === `Patient`
  const since = formatDate({
    value: isPatient ? contact.becamePatientDate : contact.becameLeadDate,
    dateOnly: true,
  })

  return (
    <Button variant="card" onClick={onOpen}>
      <ContactNameIcon
        contact={{ name: contact.name ?? name } as ContactJson}
        heightClassName="h-10"
        widthClassName="w-10"
        textClassName="text-blue-600"
        sizeStyle={{ fontSize: 14 }}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-y-0.5">
        <span className="flex min-w-0 items-center gap-x-2">
          <span className="min-w-0 truncate text-body-md font-semibold text-strong">{name}</span>
          {lifecycle && (
            <span
              className={classNames(
                `shrink-0 rounded-full px-2 py-0.5 text-label-xs font-medium`,
                isPatient ? `bg-green-50 text-green-700` : `bg-surface-muted text-default`,
              )}
            >
              {lifecycle}
            </span>
          )}
        </span>
        {since && (
          <span className="truncate text-label-xs text-subtle">
            {isPatient ? `Patient` : `Lead`} since {since}
          </span>
        )}
      </span>
      <ChevronRightIcon
        className="h-4 w-4 shrink-0 text-minimal transition-colors group-hover/button:text-default"
        strokeWidth={2}
      />
    </Button>
  )
}

/**
 * Body for the potential callers dialog, opened through `showModal`. Everyone here holds the ringing
 * number, usually a family on one mobile, so each gets the same card and nobody is ranked the caller.
 */
export function IncomingCallMatchesModal({
  call,
  contacts,
  locationName,
  onPick,
}: {
  call: IncomingCall
  contacts: IncomingCallContact[]
  locationName?: string
  onPick: (contactId: string) => void
}): ReactElement {
  const phone = formatPhoneNumber(call.callerPhone) ?? call.callerPhone
  const detail = [...callDetailParts(call), locationName].filter(Boolean).join(` · `)

  return (
    <div className="flex flex-col gap-y-4 p-6">
      <div className="flex flex-col gap-y-1">
        <h2 className="text-heading-sm font-semibold text-strong">Potential callers</h2>
        <p className="text-body-sm text-subtle">
          {contacts.length === 1 ? `1 contact shares` : `${contacts.length} contacts share`} {phone}
        </p>
        {(detail || call.callerName) && (
          <p className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-label-xs text-subtle">
            {call.callerName && (
              <span className="rounded bg-surface-muted px-1.5 py-0.5 font-medium text-default">
                Caller ID {call.callerName}
              </span>
            )}
            {detail && <span className="min-w-0 truncate">{detail}</span>}
          </p>
        )}
      </div>

      <ul className="-mx-1 flex max-h-[60vh] flex-col gap-y-2 overflow-y-auto px-1 py-1">
        {contacts.map((contact) => (
          <li key={contact.id}>
            <ContactMatchCard contact={contact} onOpen={() => onPick(contact.id)} />
          </li>
        ))}
      </ul>
    </div>
  )
}
